/**
 * AG-UI wire contract for the Ticlick assistant panel.
 *
 * Wire format (from plan.md §"The AG-UI event contract"):
 *   SSE frame: `data: {"type": "<TYPE>", ...camelCase fields}`
 *   AG-UI is THE /chat protocol (the ?protocol negotiation param is retired).
 *
 * snake_case appears ONLY inside `ConfirmationDraft` — that object is the
 * unchanged legacy payload forwarded verbatim from the backend interrupt.
 */

// ── Reuse the existing draft type so confirmation-card.tsx can import once ──

/**
 * Re-exported from `assistant.types.ts` (single source of truth — no drift).
 * snake_case fields are intentional: this is the raw backend payload.
 * The `confirmation_token` is the pending-action id (approve posts it back;
 * reject cancels it via the pending-action cancel endpoint).
 */
export type { ConfirmationDraft } from './assistant.types';
import type { ConfirmationDraft } from './assistant.types';
import {
  isPresentationBlock,
  type PresentationEventValue,
} from './presentation.types';

/**
 * Payload carried by the CUSTOM `confirmation_required` event.
 * `conversationId` is camelCase (the CUSTOM wrapper field),
 * `draft` retains snake_case internals (legacy backend payload, kept as-is).
 */
export interface ConfirmationPayload {
  conversationId: number;
  draft: ConfirmationDraft;
}

// ── AG-UI discriminated union ────────────────────────────────────────────────

export interface AguiRunStarted {
  type: 'RUN_STARTED';
  runId: string;
  conversationId: number;
}

export interface AguiToolCallStart {
  type: 'TOOL_CALL_START';
  toolCallId: string;
  toolCallName: string;
}

export interface AguiToolCallEnd {
  type: 'TOOL_CALL_END';
  toolCallId: string;
  /** `paused` = staged and waiting on the user's confirmation, NOT done. */
  status:
    | 'ok'
    | 'denied'
    | 'error'
    | 'paused'
    | 'cancelled'
    | 'delivery_unknown'
    | 'partial_delivery';
}

export interface AguiTextMessageStart {
  type: 'TEXT_MESSAGE_START';
  messageId: string;
}

export interface AguiTextMessageContent {
  type: 'TEXT_MESSAGE_CONTENT';
  messageId: string;
  delta: string;
}

export interface AguiTextMessageEnd {
  type: 'TEXT_MESSAGE_END';
  messageId: string;
}

export interface AguiCustomConfirmation {
  type: 'CUSTOM';
  name: 'confirmation_required';
  value: ConfirmationPayload;
}

/**
 * CUSTOM `presentation` event: structured CRM data rendered by native
 * components. `value` is a presentation block plus the `messageId` of the
 * assistant text message it correlates to.
 */
export interface AguiCustomPresentation {
  type: 'CUSTOM';
  name: 'presentation';
  value: PresentationEventValue;
}

/**
 * CUSTOM `messages_superseded`: take back text already shown.
 *
 * The answer streams while it is being written, so it reaches the screen before the
 * server's grounding check has passed judgement on it. Almost always it stands; when
 * it does not — the check sends it back to be rewritten, or swaps in the safe
 * fallback — this names the drafts that are no longer the answer.
 */
export interface AguiCustomSuperseded {
  type: 'CUSTOM';
  name: 'messages_superseded';
  value: { messageIds: string[] };
}

/**
 * CUSTOM `progress_note`: what the model wrote before calling a tool ("Let me
 * pull the invoices…"). Not the answer — a small progress line shown above the
 * chip of the tool call it introduces (`toolCallId`). Never retracted.
 */
export interface AguiCustomProgressNote {
  type: 'CUSTOM';
  name: 'progress_note';
  value: { messageId: string; text: string; toolCallId: string };
}

/**
 * CUSTOM `answer_status`: the answer is written and the server is checking it
 * against the records before it is shown. Only checked answers reach the
 * panel; this is the "Checking…" moment in between.
 */
export interface AguiCustomAnswerStatus {
  type: 'CUSTOM';
  name: 'answer_status';
  value: { phase: 'checking' };
}

export interface AguiRunFinished {
  type: 'RUN_FINISHED';
  runId: string;
}

export interface AguiRunError {
  type: 'RUN_ERROR';
  runId: string;
  message: string;
}

/**
 * Discriminated union over all AG-UI event types.
 * The `type` field is inside the JSON object (not in `event:` SSE header).
 */
export type AguiEvent =
  | {
      type: 'CUSTOM';
      name: 'final_message';
      value: { messageId: string; text: string; blocks: unknown[] };
    }
  | AguiRunStarted
  | AguiToolCallStart
  | AguiToolCallEnd
  | AguiTextMessageStart
  | AguiTextMessageContent
  | AguiTextMessageEnd
  | AguiCustomConfirmation
  | AguiCustomPresentation
  | AguiCustomSuperseded
  | AguiCustomProgressNote
  | AguiCustomAnswerStatus
  | AguiRunFinished
  | AguiRunError;

// ── Runtime type guard ───────────────────────────────────────────────────────

const AGUI_TYPES = new Set<string>([
  'RUN_STARTED',
  'TOOL_CALL_START',
  'TOOL_CALL_END',
  'TEXT_MESSAGE_START',
  'TEXT_MESSAGE_CONTENT',
  'TEXT_MESSAGE_END',
  'CUSTOM',
  'RUN_FINISHED',
  'RUN_ERROR',
]);

/** A server row id: conversations and pending actions are numbered from 1. */
function isId(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) > 0;
}

/** Exact-key check for variants whose contract FORBIDS extra fields (tool events). */
function hasExactKeys(
  obj: Record<string, unknown>,
  allowed: string[]
): boolean {
  const keys = Object.keys(obj);
  return (
    keys.length === allowed.length && keys.every((k) => allowed.includes(k))
  );
}

/**
 * Runtime type guard: checks the `type` discriminator and the required fields
 * per variant. Tool-call variants are EXACT-key checked (no-args invariant).
 */
export function isAguiEvent(x: unknown): x is AguiEvent {
  if (typeof x !== 'object' || x === null) return false;
  // `Record<string, unknown>` is safe here — we verify every field we access.
  const obj = x as Record<string, unknown>;
  const t = obj['type'];
  if (typeof t !== 'string' || !AGUI_TYPES.has(t)) return false;

  switch (t) {
    case 'RUN_STARTED':
      return typeof obj['runId'] === 'string' && isId(obj['conversationId']);

    case 'TOOL_CALL_START':
      // STRICT: tool events must carry the tool NAME only — an extra field
      // (args/payload/result…) means the backend is leaking data it must not.
      return (
        typeof obj['toolCallId'] === 'string' &&
        typeof obj['toolCallName'] === 'string' &&
        hasExactKeys(obj, ['type', 'toolCallId', 'toolCallName'])
      );

    case 'TOOL_CALL_END':
      return (
        typeof obj['toolCallId'] === 'string' &&
        (obj['status'] === 'ok' ||
          obj['status'] === 'denied' ||
          obj['status'] === 'error' ||
          obj['status'] === 'paused' ||
          obj['status'] === 'cancelled' ||
          obj['status'] === 'delivery_unknown' ||
          obj['status'] === 'partial_delivery') &&
        hasExactKeys(obj, ['type', 'toolCallId', 'status'])
      );

    case 'TEXT_MESSAGE_START':
      return typeof obj['messageId'] === 'string';

    case 'TEXT_MESSAGE_CONTENT':
      return (
        typeof obj['messageId'] === 'string' && typeof obj['delta'] === 'string'
      );

    case 'TEXT_MESSAGE_END':
      return typeof obj['messageId'] === 'string';

    case 'CUSTOM': {
      if (obj['name'] === 'final_message') {
        const value = obj['value'] as Record<string, unknown> | null;
        return (
          !!value &&
          typeof value.messageId === 'string' &&
          typeof value.text === 'string' &&
          Array.isArray(value.blocks)
        );
      }
      // `presentation`: structured CRM data. The value must be a valid block
      // AND carry the correlating messageId.
      if (obj['name'] === 'presentation') {
        const v = obj['value'];
        return (
          isPresentationBlock(v) &&
          typeof (v as Record<string, unknown>)['messageId'] === 'string'
        );
      }
      // `messages_superseded`: a retraction. Validated strictly — silently
      // dropping a malformed one would leave a rejected answer on screen.
      if (obj['name'] === 'messages_superseded') {
        const v = obj['value'];
        if (typeof v !== 'object' || v === null) return false;
        const ids = (v as Record<string, unknown>)['messageIds'];
        return Array.isArray(ids) && ids.every((id) => typeof id === 'string');
      }
      if (obj['name'] === 'progress_note') {
        const v = obj['value'] as Record<string, unknown> | null;
        return (
          typeof v === 'object' &&
          v !== null &&
          typeof v.messageId === 'string' &&
          typeof v.text === 'string' &&
          typeof v.toolCallId === 'string'
        );
      }
      // Only the phases this client knows; a newer one is dropped, not fatal.
      if (obj['name'] === 'answer_status') {
        const v = obj['value'] as Record<string, unknown> | null;
        return typeof v === 'object' && v !== null && v.phase === 'checking';
      }
      if (obj['name'] !== 'confirmation_required') return false;
      const v = obj['value'];
      if (typeof v !== 'object' || v === null) return false;
      const val = v as Record<string, unknown>;
      if (!isId(val['conversationId'])) return false;
      const d = val['draft'];
      if (typeof d !== 'object' || d === null) return false;
      return isId((d as Record<string, unknown>)['confirmation_token']);
    }

    case 'RUN_FINISHED':
      return typeof obj['runId'] === 'string';

    case 'RUN_ERROR':
      return (
        typeof obj['runId'] === 'string' && typeof obj['message'] === 'string'
      );

    default:
      return false;
  }
}
